import Konva from "konva";
import { GenericNodeEvents } from "./common";
import type { SeatState2 } from "./types";
import type { Row2 } from "./row";

const FONT_SIZE = 8;
const DEFAULT_STROKE_WIDTH = 1;
const PADDING = 2;

export type SeatShape = "circle" | "rect";

export type SeatOptions = (
  | {
      shape: "circle";
      shapeOptions: Konva.CircleConfig;
    }
  | { shape: "rect"; shapeOptions: Konva.RectConfig }
) & {
  rowGroup: Konva.Group;
};

export class Seat2 extends GenericNodeEvents<Seat2, SeatState2> {
  private id!: string;
  private readonly rowGroup: Konva.Group;
  private readonly state: SeatState2;
  private readonly row: Row2;
  private container!: Konva.Rect;
  private label!: Konva.Text;
  private seatGroup!: Konva.Group;
  private index!: number

  get seatNumber() {
    return this.index;
  }

  get nodeId() {
    return this.id;
  }

  get rowId() {
    return this.row.nodeId;
  }

  get rowLabel() {
    return this.row.currentState.label;
  }

  get currentState() {
    return this.state;
  }

  get x() {
    return this.container.x();
  }

  constructor(index: number, rowGroup: Konva.Group, state: SeatState2, row: Row2) {
    const group = new Konva.Group();
    super(group, "seat");
    this.target = this;
    this.state = state;
    this.setDefaultState();
    this.row = row;
    this.index = index;

    this.rowGroup = rowGroup;
  }

  create() {
    const state = this.state;
    this.id = state.id!;
    this.container = new Konva.Rect();
    this.loadContainer();

    this.label = new Konva.Text();
    this.seatGroup = new Konva.Group();
    this.nodeContainer = this.seatGroup;
    this.loadLabel();

    this.seatGroup.add(this.container, this.label);
    this.rowGroup.add(this.seatGroup);
  }

  update() {
    const state = this.state;
    this.id = state.id!;

    this.loadContainer();

    this.loadLabel();
  }

  private loadContainer() {
    const state = this.state;
    this.container.setAttrs({
      ...state.position,
      ...state,
      strokeWidth: this.state.strokeWidth || DEFAULT_STROKE_WIDTH,
      cornerRadius: this.state.borderRadius ?? 1000,
      width: state.width + 2 * PADDING,
      height: state.height + 2 * PADDING,
    });
    this.container.offsetY(this.container.width() / 2);
  }

  private loadLabel() {
    this.label.setAttrs({
      text: this.state.label,
      fill: this.state.fillText || "black",
      fontSize: this.state.fontSize ?? FONT_SIZE,
      fontStyle: "bold",
      rotation: this.state.rotationText,
      x: this.container.x() + this.container.width() / 2,
      y: this.container.y(),
      verticalAlign: "center",
      scale: this.state.scale,
    });

    this.label.setAttrs({
      offsetX: this.label.width() / 2,
      offsetY: this.label.height() / 2,
    });
  }

  private setDefaultState() {
    this.state.fill = this.state.fill || "#fff";
    this.state.strokeWidth = this.state.strokeWidth ?? DEFAULT_STROKE_WIDTH;
    this.state.stroke = this.state.stroke || "#FFFFFF";
    this.state.scale = { x: 1, y: 1 };
  }
}
